import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormGroup,
  Button,
  Input,
  Card,
  CardHeader,
  CardBody,
  Form,
  InputGroup,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TabPane,
  TabContent,
  Nav,
  NavItem,
  NavLink,
  Label
} from 'reactstrap';
import _ from 'lodash/fp';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { SEND_FEEDBACK } from '@api';
import { useMutation } from '@apollo/client';
import { Icon } from '@assets';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '@sparklib';
import classnames from 'classnames';
import TutorialList from '../../website/TutorialList';

export default function FeedbackForm(props) {
  const feedbackTabs = [
		{
			tabId: 'help',
			name: 'Help'
		},
		{
			tabId: 'suggestMissingProperty',
			name: 'Data Correction / Suggest Missing Property'
		},
		{
			tabId: 'suggestMissingMultipleProperties',
			name: 'Suggest Missing Multiple Properties'
		},
    {
      tabId: 'tutorials',
      name: 'Tutorial'
    }
	];
  const closeBtn = (
    <button
      className="close"
      onClick={props.parentRef}
      style={{ color: 'var(--white)', opacity: 1, textShadow: 'none' }}
    >
      &times;
    </button>
  );
  const [activeTab, setActiveTab] = useState('help');
  const [feedbackFiles, setFeedbackFiles] = useState([]);
  const { register, handleSubmit, errors, reset, getValues } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const [sendFeedback, { loading, error }] = useMutation(SEND_FEEDBACK, {
    context: { hasUpload: true },
    onCompleted: (data) => {
      if (data.sendFeedback.errors && data.sendFeedback.errors.length > 0) {
        toast(data.sendFeedback.message, { type: 'error' });
      } else {
        toast(data.sendFeedback.message, { type: 'success' });
        props.parentRef();
        reset();
        setFeedbackFiles([]);
      }
    },
    onError: (e) => {
      console.log(e, 'error');
    },
  });

  const onSubmit = (data, e) => {
    const currentTab = feedbackTabs.find(tab => tab.tabId === activeTab);
    const feedbackType = currentTab ? currentTab.name : '';
    console.log('data', data);
    console.log('feedbackFiles', feedbackFiles);
    console.log('feedbackType', feedbackType);
    sendFeedback({
      variables: {
        ...data,
        feedbackFiles: feedbackFiles,
        feedbackType: feedbackType
      },
    });
  };

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks
    feedbackFiles &&
      feedbackFiles.map((feedbackFile) => URL.revokeObjectURL(feedbackFile.preview));
  }, [feedbackFiles]);

  return (
    <>
      <Modal isOpen={props.displayModal} toggle={props.parentRef} size={'xl'} centered={true} scrollable={true} backdrop={true} keyboard={true} contentClassName='rounded'>
        <ModalHeader toggle={props.parentRef} close={closeBtn} style={{ backgroundColor: 'var(--gray-700)', color: 'var(--white)' }}>Send us your feedback</ModalHeader>
        <ModalBody style={{ height: '500px', overflowY: 'auto' }}>
          <Form onSubmit={handleSubmit(onSubmit)} className='table-tabs long-modal'>
            <Nav tabs className='justify-content-center'>
              {
                feedbackTabs.map(tab => {
                  return (
                    <NavItem key={tab.tabId} class="mx-w-100">
                      <NavLink
                        className={classnames({
                          active: activeTab === tab.tabId,
                        })}
                        onClick={() => {
                          setActiveTab(tab.tabId);
                        }}
                      >
                        {tab.name}
                      </NavLink>
                    </NavItem>
                  )
                })
              }
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId='help'>
                <Card>
                  <CardBody>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        innerRef={register({
                          required: activeTab === 'help',
                        })}
                        placeholder=''
                        name='title'
                        id='title'
                      />
                      {_.get('title.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                      <Label for="title">Subject</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='textarea'
                        placeholder=''
                        id='description'
                        name='description'
                        rows='5'
                        innerRef={register({
                          required: activeTab === 'help',
                        })}
                      />
                      {_.get('description.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                      <Label for="description">Description</Label>
                    </FormGroup>
                    <FormGroup>
                      <Label for="feedbackFilesDropZone">Attach Files</Label>
                      <InputGroup>
                        {(feedbackFiles) &&
                          feedbackFiles.map((feedbackFile, index) => (
                            <>
                              <Col md='2' key={index}>
                                <img
                                  src={
                                    feedbackFile ? feedbackFile.preview : ''
                                  }
                                  alt={feedbackFile.name}
                                  className='thumb mr-2'
                                />
                                <Icon
                                  icon='times'
                                  key={index}
                                  className='remove-item my-profile'
                                  onClick={() => {
                                    setFeedbackFiles(
                                      feedbackFiles.filter((feedbackFile, fbf) => fbf !== index)
                                    );
                                  }}
                                />
                              </Col>
                            </>
                          )
                        )}
                        <Dropzone
                          name='feedbackFilesDropZone'
                          accept='image/jpeg, image/png, image/jpg, *.pdf'
                          multiple={true}
                          onDrop={(acceptedFiles) => {
                            acceptedFiles.length > 0 &&
                              setFeedbackFiles((feedbackFiles) => [
                                ...feedbackFiles,
                                ...acceptedFiles.map((file) =>
                                  Object.assign(file, {
                                    preview: URL.createObjectURL(file),
                                  })
                                ),
                              ]);
                          }}
                        >
                          {({
                            getRootProps,
                            getInputProps,
                            isDragActive,
                            isDragAccept,
                            isDragReject,
                          }) => {
                            const style = useMemo(
                              () => ({
                                ...baseStyle,
                                ...(isDragActive ? activeStyle : {}),
                                ...(isDragAccept ? acceptStyle : {}),
                                ...(isDragReject ? rejectStyle : {}),
                              }),
                              [isDragActive, isDragReject, isDragAccept]
                            );
                            return (
                              <span className='flex-grow'>
                                <div {...getRootProps({ style })}>
                                  <input {...getInputProps()} />
                                  <div className='text-center w-100'>
                                    Click to choose files or drag them here.<br />
                                    <small>
                                      <em>
                                        JPEG, JPG, PNG or PDF files supported.<br/>
                                        Maximum file sizes of 10 MB.
                                      </em>
                                    </small>
                                  </div>
                                </div>
                              </span>
                            );
                          }}
                        </Dropzone>
                      </InputGroup>
                    </FormGroup>
                  </CardBody>
                </Card>
              </TabPane>
              <TabPane tabId='suggestMissingProperty'>
                <Card>
                  <CardBody>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyName'
                        id='missingPropertyName'
                        innerRef={register({
                          required: activeTab === 'suggestMissingProperty',
                        })}
                      />
                      {_.get('missingPropertyName.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                      <Label for="missingPropertyName">Property Name</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">

                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyCity'
                        id='missingPropertyCity'
                        innerRef={register({
                          required: activeTab === 'suggestMissingProperty',
                        })}
                      />
                      {_.get('missingPropertyCity.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                      <Label for="missingPropertyCity">City</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyNeighborhood'
                        id='missingPropertyNeighborhood'
                        innerRef={register({
                          required: activeTab === 'suggestMissingProperty',
                        })}
                      />
                      {_.get('missingPropertyNeighborhood.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                      <Label for="missingPropertyNeighborhood">Neighborhood</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyWebLink'
                        id='missingPropertyWebLink'
                        innerRef={register({
                          required: activeTab === 'suggestMissingProperty',
                        })}
                      />
                      {_.get('missingPropertyWebLink.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                      <Label for="missingPropertyWebLink">Website URL</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyEmail'
                        id='missingPropertyEmail'
                        innerRef={register}
                      />
                      <Label for="missingPropertyEmail">Leasing Email</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyManagementCompany'
                        id='missingPropertyManagementCompany'
                        innerRef={register}
                      />
                      <Label for="missingPropertyManagementCompany">Management Company</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyCommissionPolicy'
                        id='missingPropertyCommissionPolicy'
                        innerRef={register}
                      />
                      <Label for="missingPropertyCommissionPolicy">Commission Policy</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='text'
                        placeholder=''
                        name='missingPropertyEscortPolicy'
                        id='missingPropertyEscortPolicy'
                        innerRef={register}
                      />
                      <Label for="missingPropertyEscortPolicy">Escort Policy</Label>
                    </FormGroup>
                    <FormGroup className="form-floating">
                      <Input
                        type='textarea'
                        placeholder=''
                        id='missingPropertySpecials'
                        name='missingPropertySpecials'
                        rows='5'
                        innerRef={register}
                      />
                      <Label for="missingPropertySpecials">Specials / Promotions</Label>
                    </FormGroup>
                  </CardBody>
                </Card>
              </TabPane>
              <TabPane tabId='suggestMissingMultipleProperties'>
                <Card>
                  <CardBody>
                    <FormGroup>
                      <Label for="missingMultiplePropertiesInformation">Information</Label>
                      <Input
                        type='textarea'
                        placeholder="Write the information here. It's ok if it's messy. We will get working on this right away and will follow up if needed."
                        id='missingMultiplePropertiesInformation'
                        name='missingMultiplePropertiesInformation'
                        rows='5'
                        innerRef={register({
                          required: activeTab === 'suggestMissingMultipleProperties',
                        })}
                      />
                      {_.get('missingMultiplePropertiesInformation.type', errors) === 'required' && (
                        <p className='input-error'>* required.</p>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label for="missingPropertiesFilesDropZone">OR Upload a spreadsheet in XLXS or CSV format</Label>
                      <InputGroup>
                        {(feedbackFiles) &&
                          feedbackFiles.map((feedbackFile, index) => (
                            <>
                              <Col md='2' key={index}>
                                <img
                                  src={
                                    feedbackFile ? feedbackFile.preview : ''
                                  }
                                  alt={feedbackFile.name}
                                  className='thumb mr-2'
                                />
                                <Icon
                                  icon='times'
                                  key={index}
                                  className='remove-item my-profile'
                                  onClick={() => {
                                    setFeedbackFiles(
                                      feedbackFiles.filter((feedbackFile, fbf) => fbf !== index)
                                    );
                                  }}
                                />
                              </Col>
                            </>
                          )
                        )}
                        <Dropzone
                          name='missingPropertiesFilesDropZone'
                          accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv'
                          multiple={true}
                          onDrop={(acceptedFiles) => {
                            acceptedFiles.length > 0 &&
                              setFeedbackFiles((feedbackFiles) => [
                                ...feedbackFiles,
                                ...acceptedFiles.map((file) =>
                                  Object.assign(file, {
                                    preview: URL.createObjectURL(file),
                                  })
                                ),
                              ]);
                          }}
                        >
                          {({
                            getRootProps,
                            getInputProps,
                            isDragActive,
                            isDragAccept,
                            isDragReject,
                          }) => {
                            const style = useMemo(
                              () => ({
                                ...baseStyle,
                                ...(isDragActive ? activeStyle : {}),
                                ...(isDragAccept ? acceptStyle : {}),
                                ...(isDragReject ? rejectStyle : {}),
                              }),
                              [isDragActive, isDragReject, isDragAccept]
                            );
                            return (
                              <span className='flex-grow'>
                                <div {...getRootProps({ style })}>
                                  <input {...getInputProps()} />
                                  <div className='text-center w-100'>
                                    Click to choose files or drag them here.<br />
                                    <small>
                                      <em>
                                        XLSX or CSV files supported.<br/>
                                        Any format is accepted. No specific headers required.
                                      </em>
                                    </small>
                                  </div>
                                </div>
                              </span>
                            );
                          }}
                        </Dropzone>
                      </InputGroup>
                    </FormGroup>
                  </CardBody>
                </Card>
              </TabPane>
              <TabPane tabId='tutorials'>
                <Card>
                  <CardBody>
                    <TutorialList {...props} />
                  </CardBody>
                </Card>
              </TabPane>
            </TabContent>
          </Form>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          {['help', 'suggestMissingProperty', 'suggestMissingMultipleProperties'].includes(activeTab) && (
            <Button color="green" onClick={handleSubmit(onSubmit)}>Submit</Button>
          )}{' '}
          <Button color="secondary" onClick={props.parentRef}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
